import { useQuery } from '@tanstack/react-query'
import store from 'stores/store'
import { getMint, Mint, TOKEN_PROGRAM_ID, unpackMint } from '@solana/spl-token'
import {
  deserializeDigitalAsset,
  DigitalAsset,
  fetchDigitalAsset,
  findMasterEditionPda,
  findMetadataPda,
} from '@metaplex-foundation/mpl-token-metadata'
import useUmi from './useUmi'
import {
  lamports,
  PublicKey as UmiKey,
  RpcAccount,
  Umi,
} from '@metaplex-foundation/umi'
import { AccountInfo, PublicKey } from '@solana/web3.js'
import { milliseconds } from 'date-fns'
import { tryGetPubKey } from 'utils/governance/tools'

export type MintInfoItem = {
  mintInfo?: {
    supply: number
    address: PublicKey
    mintAuthority: PublicKey | null
    decimals: number
    isInitialized: boolean
    freezeAuthority: PublicKey | null
    tlvData: Buffer
  }
  metadata?: DigitalAsset
}

const fetchMintInfo = async (
  tokenMint: string,
  tokenProgram: string,
  umi: Umi,
): Promise<MintInfoItem | null> => {
  try {
    const connection = store.getState().connection
    const mintAddress = new PublicKey(tokenMint)
    const promises: [
      Promise<Mint | undefined>,
      Promise<DigitalAsset | undefined>,
    ] = [
      getMint(
        connection,
        mintAddress,
        'confirmed',
        tryGetPubKey(tokenProgram) ?? TOKEN_PROGRAM_ID,
      ).catch((e) => {
        console.error(
          `failed to fetch mint info from rpc: mint=${tokenMint}`,
          e,
        )
        return undefined
      }),
      fetchDigitalAsset(umi, tokenMint as UmiKey).catch((e) => {
        console.error(
          `failed to fetch mint info from umi: mint=${tokenMint}`,
          e,
        )
        return undefined
      }),
    ]
    const [mintInfoData, metadata] = await Promise.all(promises)
    if (!mintInfoData && !metadata) {
      return null
    }
    const mintInfo = mintInfoData && {
      ...mintInfoData,
      supply:
        mintInfoData.supply && mintInfoData.decimals
          ? Number(mintInfoData.supply) / Math.pow(10, mintInfoData.decimals)
          : 0,
    }
    return { mintInfo, metadata }
  } catch (e) {
    console.error(`failed to fetch mint info: mint=${tokenMint}`, e)
    return null
  }
}

// At most can be used with 33 separate tokens
export async function fetchMultipleMintInfoAndMetadata(
  mints: string[],
  umi: Umi,
): Promise<MintInfoItem[]> {
  const connection = store.getState().connection
  const allMints: PublicKey[] = []
  mints.forEach((mint) => {
    const mintAddress = new PublicKey(mint)
    const [metadataAddress] = findMetadataPda(umi, { mint: mint as UmiKey })
    const [editionAddress] = findMasterEditionPda(umi, { mint: mint as UmiKey })
    allMints.push(
      mintAddress,
      new PublicKey(metadataAddress),
      new PublicKey(editionAddress),
    )
  })

  const results = []
  const allAccounts = await connection.getMultipleAccountsInfo(allMints)

  for (let i = 0; i < allAccounts.length; i += 3) {
    const currentAccount = allAccounts[i]
    const nextAccount = allAccounts[i + 1]
    const nextNextAccount = allAccounts[i + 2]
    if (currentAccount === null || nextAccount === null) {
      continue
    }
    const a = metaplexAccount(allMints[i].toBase58(), currentAccount)
    const b = metaplexAccount(allMints[i + 1].toBase58(), nextAccount)
    const c =
      nextNextAccount === null
        ? undefined
        : metaplexAccount(allMints[i + 2].toBase58(), nextNextAccount)
    const metadata = deserializeDigitalAsset(a, b, c)
    const mintInfoData = unpackMint(allMints[i], currentAccount)
    const mintInfo = {
      ...mintInfoData,
      supply:
        mintInfoData?.supply && mintInfoData?.decimals
          ? Number(mintInfoData.supply) / Math.pow(10, mintInfoData.decimals)
          : 0,
    }
    results.push({ mintInfo, metadata })
  }
  return results
}

function metaplexAccount(
  key: string,
  accountInfo: AccountInfo<Buffer>,
): RpcAccount {
  return {
    publicKey: key as UmiKey,
    data: accountInfo.data,
    executable: accountInfo.executable,
    owner: accountInfo.owner.toBase58() as UmiKey,
    lamports: lamports(accountInfo.lamports),
  }
}

export const useMintInfo = (tokenMint: string, tokenProgram: string) => {
  const umi = useUmi()
  const {
    data: mintInfoData,
    isInitialLoading,
    isFetching,
  } = useQuery({
    queryKey: ['mint-info', tokenMint],
    queryFn: () => fetchMintInfo(tokenMint, tokenProgram, umi),
    cacheTime: milliseconds({ minutes: 30 }),
    staleTime: milliseconds({ minutes: 30 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!tokenMint,
  })

  const loading = isInitialLoading || isFetching
  return { mintInfoData, loading }
}
